/**
 * @fileoverview Voidable Transactions Animation
 *
 * Purpose: A stylised, looping recreation of Morgan's Voidable Transactions
 * investigation for the homepage feature card ("Automated investigations with
 * practitioners in the loop"). It tells a three-beat story:
 *
 *  1. Analysing - the data agent works across the whole estate: sets the
 *                 statutory window (relation-back date → date of insolvency),
 *                 analyses the bank transactions and financial ledgers, and maps
 *                 related entities & associated parties. Each step ticks
 *                 pending → processing → complete (mirrors the agent task feed),
 *                 under the violet "live stream" header from ManagedAgentLivePanel.
 *  2. Flagging  - the analysis collapses to a quantum summary and a sample of
 *                 flagged transactions drops into the table, each tagged with its
 *                 head (s588FA unfair preference, s588FDA director-related, etc.).
 *  3. Reviewing - a cursor clicks a flagged transaction; a stylised review card
 *                 (mirrors TransactionReviewModal) animates into the centre with
 *                 Accept / Reject. The cursor clicks Accept, the card closes, and
 *                 the row settles to green "Accepted", with a confirming toast.
 *
 * This is a marketing stylisation — no live data — sized to the feature-card
 * container. It reuses the real app's colour language (violet agent stream,
 * per-head badge colours, emerald accept) so it reads as Morgan.
 *
 * Respects prefers-reduced-motion by rendering the final "reviewed" state.
 *
 * Used by: FeatureCardsSection (-sections.feature-cards.tsx)
 */

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeftRight,
  CalendarRange,
  Check,
  FileSpreadsheet,
  Flag,
  Loader2,
  MessageSquare,
  MousePointer2,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/misc";

/** Stylised badge palette per voidable-transaction head (mirrors HEAD_BADGE). */
const HEAD_STYLES = {
  unfair_preference: {
    label: "Unfair Preference",
    chip: "bg-blue-100 text-blue-700",
  },
  director_related: {
    label: "Director-Related",
    chip: "bg-violet-100 text-violet-700",
  },
  uncommercial: { label: "Uncommercial", chip: "bg-amber-100 text-amber-700" },
} as const;

interface AnalysisTask {
  id: string;
  label: string;
  /** Optional second line (used for the statutory window detail). */
  detail?: string;
  icon: LucideIcon;
}

/** The investigation steps that tick through during phase 1. */
const ANALYSIS_TASKS: AnalysisTask[] = [
  {
    id: "window",
    label: "Window analysed",
    detail: "RBD 25 Jun → insolvency 4 Oct · insolvency analysis",
    icon: CalendarRange,
  },
  {
    id: "bank",
    label: "Analysing 346,782 bank transactions",
    icon: ArrowLeftRight,
  },
  {
    id: "ledgers",
    label: "Analysing the company's financial ledgers",
    icon: FileSpreadsheet,
  },
  {
    id: "parties",
    label: "Mapping related entities & associated parties",
    icon: Users,
  },
] as const;

interface FlagRow {
  id: string;
  counterparty: string;
  relationship?: string;
  head: keyof typeof HEAD_STYLES;
  provision: string;
  amount: string;
}

/** A sample of flags that drop in during phase 2. f2 is the one reviewed. */
const FLAGS: FlagRow[] = [
  {
    id: "f1",
    counterparty: "Apex Trade Supplies",
    head: "unfair_preference",
    provision: "s588FA",
    amount: "$84,200",
  },
  {
    id: "f2",
    counterparty: "Director Loan — J. Hale",
    relationship: "Related Party",
    head: "director_related",
    provision: "s588FDA",
    amount: "$126,500",
  },
  {
    id: "f3",
    counterparty: "Northbridge Holdings",
    relationship: "Associated Entity",
    head: "uncommercial",
    provision: "s588FB",
    amount: "$47,900",
  },
  {
    id: "f4",
    counterparty: "Meridian Freight Co",
    head: "unfair_preference",
    provision: "s588FA",
    amount: "$63,400",
  },
  {
    id: "f5",
    counterparty: "Hale Family Trust",
    relationship: "Related Party",
    head: "director_related",
    provision: "s588FDA",
    amount: "$58,750",
  },
] as const;

/** Stylised review-card content for the flag the cursor approves (f2). */
const REVIEW = {
  name: "Director Loan — J. Hale",
  provisions: "s588FDA · s588FE",
  relationship: "Related Party — repayment inside relation-back period",
  amount: "$126,500",
  recoverable: "$126,500",
  assessment:
    "Loan repaid to director J. Hale on 12 Aug 2024, inside the relation-back period. As a related-party transaction the s588FE(4) presumption of insolvency applies and the repayment conferred a preference over unsecured creditors.",
  alsoCaptured: ["Unfair Preference", "Unfair Loans"],
} as const;

const TOTAL_TX = 867998;
/** Total potential voidable transactions surfaced by the analysis. */
const IDENTIFIED = 172;
/** Distinct voidable-transaction heads (claims) the flags fall under. */
const CLAIM_COUNT = 6;
/** Headline quantum of estimated recoveries across the flagged transactions. */
const EST_RECOVERIES = "$2.48M";

// --- Timing (module scope so the progress line stays in sync with the run) ---
/** Per analysis-task processing time. */
const TASK_MS = 760;
/** Stagger between flagged rows dropping in. */
const FLAG_STAGGER_MS = 320;
const ANALYSIS_END_MS = 250 + ANALYSIS_TASKS.length * TASK_MS + 250;
const FLAGS_END_MS =
  ANALYSIS_END_MS + 250 + FLAGS.length * FLAG_STAGGER_MS + 400;
/** Tail held on the reviewed state before the cycle repeats. */
const REST_MS = 7400;
/** Total length of one play-through — drives the progress line + orchestrator. */
export const VOIDABLE_ANIMATION_CYCLE_MS = FLAGS_END_MS + REST_MS;

type TaskStatus = "pending" | "processing" | "complete";
type Phase =
  "analysing" | "flagging" | "opening" | "review" | "accepting" | "done";

/** The id of the flag the cursor reviews & approves. */
const APPROVED_ID = "f2";

interface VoidableTransactionsAnimationProps {
  /**
   * When false, the animation rests on the populated "transactions identified"
   * view (no playback, no progress line). The parent orchestrator toggles this
   * so only one card animates at a time.
   */
  isActive?: boolean;
}

export function VoidableTransactionsAnimation({
  isActive = true,
}: VoidableTransactionsAnimationProps) {
  const reducedMotion = useReducedMotion() ?? false;

  const [phase, setPhase] = useState<Phase>("analysing");
  const [taskStatuses, setTaskStatuses] = useState<TaskStatus[]>(
    ANALYSIS_TASKS.map(() => "pending"),
  );
  const [scanned, setScanned] = useState(0);
  const [visibleFlags, setVisibleFlags] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  // Bumped at the start of every play-through so the progress line restarts.
  const [cycle, setCycle] = useState(0);

  // Cursor position (x %, y px within the body) for the review gesture.
  const [cursor, setCursor] = useState({ x: 50, y: 112, visible: false });

  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const intervals = useRef<Array<ReturnType<typeof setInterval>>>([]);

  useEffect(() => {
    // Resting / static state: the populated "transactions identified" view.
    if (reducedMotion || !isActive) {
      timers.current.forEach(clearTimeout);
      intervals.current.forEach(clearInterval);
      timers.current = [];
      intervals.current = [];
      setPhase("done");
      setTaskStatuses(ANALYSIS_TASKS.map(() => "complete"));
      setScanned(TOTAL_TX);
      setVisibleFlags(FLAGS.length);
      setModalOpen(false);
      setAccepted(false);
      setCursor({ x: 50, y: 112, visible: false });
      return;
    }

    const clearAll = () => {
      timers.current.forEach(clearTimeout);
      intervals.current.forEach(clearInterval);
      timers.current = [];
      intervals.current = [];
    };

    const after = (ms: number, fn: () => void) => {
      timers.current.push(setTimeout(fn, ms));
    };

    const setTask = (index: number, status: TaskStatus) =>
      setTaskStatuses((prev) => {
        const next = [...prev];
        next[index] = status;
        return next;
      });

    const runCycle = () => {
      // Reset
      setPhase("analysing");
      setCycle((c) => c + 1);
      setTaskStatuses(ANALYSIS_TASKS.map(() => "pending"));
      setScanned(0);
      setVisibleFlags(0);
      setModalOpen(false);
      setAccepted(false);
      setCursor({ x: 50, y: 112, visible: false });

      // Ticking counter during the bank-analysis step.
      const counter = setInterval(() => {
        setScanned((prev) => {
          const next = prev + Math.ceil(TOTAL_TX / 40);
          return next >= TOTAL_TX ? TOTAL_TX : next;
        });
      }, 50);
      intervals.current.push(counter);
      after(3400, () => {
        clearInterval(counter);
        setScanned(TOTAL_TX);
      });

      // Phase 1: analysis tasks tick pending → processing → complete.
      ANALYSIS_TASKS.forEach((_, i) => {
        after(250 + i * TASK_MS, () => setTask(i, "processing"));
        after(250 + i * TASK_MS + TASK_MS - 140, () => setTask(i, "complete"));
      });

      const analysisEnd = ANALYSIS_END_MS;

      // Phase 2: flags drop into the table.
      after(analysisEnd, () => setPhase("flagging"));
      FLAGS.forEach((_, i) => {
        after(analysisEnd + 250 + i * FLAG_STAGGER_MS, () =>
          setVisibleFlags(i + 1),
        );
      });

      const flagsEnd = FLAGS_END_MS;

      // Phase 3: cursor moves to the director-related flag (2nd row) & clicks.
      after(flagsEnd, () => {
        setPhase("opening");
        setCursor({ x: 40, y: 150, visible: true });
      });
      // Review card animates in and lingers so it can be read.
      after(flagsEnd + 750, () => {
        setPhase("review");
        setModalOpen(true);
      });
      // Cursor travels to the Accept button in the card.
      after(flagsEnd + 2400, () => setCursor({ x: 20, y: 244, visible: true }));
      // Click Accept.
      after(flagsEnd + 3200, () => setPhase("accepting"));
      after(flagsEnd + 3450, () => setAccepted(true));
      // Card holds on the accepted state, then closes; row is now green.
      after(flagsEnd + 4600, () => {
        setModalOpen(false);
        setPhase("done");
        setCursor((c) => ({ ...c, visible: false }));
      });

      // Hold, then loop.
      after(flagsEnd + REST_MS, runCycle);
    };

    runCycle();
    return clearAll;
  }, [reducedMotion, isActive]);

  const analysing = phase === "analysing";

  const playing = isActive && !reducedMotion;

  return (
    <div className="absolute inset-0 overflow-hidden rounded-lg bg-white dark:bg-card">
      {/* Progress line — fills along the bottom while this animation plays */}
      {playing && (
        <motion.div
          key={cycle}
          className="absolute inset-x-0 bottom-0 z-50 h-[3px] bg-primary"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{
            duration: VOIDABLE_ANIMATION_CYCLE_MS / 1000,
            ease: "linear",
          }}
          style={{ originX: 0 }}
        />
      )}
      <div className="flex h-full w-full flex-col">
        {/* Title bar */}
        <div className="flex items-center gap-2 border-b border-primary/10 bg-primary/[0.03] px-3 py-2">
          <div className="flex gap-1.5">
            <span className="h-2 w-2 rounded-full bg-red-400/70" />
            <span className="h-2 w-2 rounded-full bg-amber-400/70" />
            <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
          </div>
          <span className="ml-1 text-[11px] font-semibold text-primary">
            Voidable Transactions
          </span>
          {!analysing && (
            <span className="ml-auto flex items-center gap-1 rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
              <Flag className="h-2.5 w-2.5" />
              {IDENTIFIED}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-3">
          {/* Agent panel (mirrors ManagedAgentLivePanel) */}
          <div className="shrink-0 rounded-md border border-violet-200 bg-violet-50/60 dark:border-violet-500/25 dark:bg-violet-500/10">
            <div className="flex items-center gap-1.5 border-b border-violet-200/70 px-2.5 py-1.5 dark:border-violet-500/20">
              <span className="text-[10px] font-semibold text-violet-900 dark:text-violet-200">
                Voidable Transactions Agent
              </span>
              {analysing ? (
                <Loader2 className="h-3 w-3 animate-spin text-violet-500" />
              ) : (
                <Check className="h-3 w-3 text-emerald-600" />
              )}
              <span className="ml-auto font-mono text-[9px] tabular-nums text-violet-500">
                {scanned.toLocaleString()} / {TOTAL_TX.toLocaleString()}
              </span>
            </div>

            {/* While analysing: full task checklist. After: one summary line. */}
            {analysing ? (
              <div className="space-y-1 px-2.5 py-2">
                {ANALYSIS_TASKS.map((task, i) => {
                  const status = taskStatuses[i];
                  const Icon = task.icon;
                  return (
                    <div
                      key={task.id}
                      className={cn(
                        "flex items-start gap-1.5 text-[10px] leading-snug transition-opacity",
                        status === "pending"
                          ? "text-violet-700/35 dark:text-violet-300/30"
                          : "text-violet-900 dark:text-violet-200",
                      )}
                    >
                      <span className="mt-px flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                        {status === "complete" ? (
                          <Check
                            className="h-3 w-3 text-emerald-600"
                            strokeWidth={3}
                          />
                        ) : status === "processing" ? (
                          <Loader2 className="h-3 w-3 animate-spin text-violet-500" />
                        ) : (
                          <Icon className="h-3 w-3 text-violet-400/60" />
                        )}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate">{task.label}</span>
                        {task.detail && (
                          <span className="block truncate text-[8.5px] text-violet-600/70 dark:text-violet-300/60">
                            {task.detail}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-1.5 px-2.5 py-2 text-[10px] font-semibold text-violet-900 dark:text-violet-200"
              >
                <Flag className="h-3 w-3 shrink-0 text-red-500" />
                <span className="truncate">
                  {IDENTIFIED} potential voidable transactions identified
                </span>
              </motion.div>
            )}
          </div>

          {/* Flagged transactions table — fills the remaining height */}
          <div className="mt-2.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-primary/10">
            {/* Header — title left, estimated-recoveries quantum right */}
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-primary/10 bg-primary/[0.03] px-2.5 py-1.5">
              <p className="text-[10px] font-semibold leading-tight text-primary">
                Flagged Transactions
              </p>
              {!analysing && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-[8.5px] font-medium leading-tight text-primary/55"
                >
                  {IDENTIFIED} transactions across {CLAIM_COUNT} claims ·{" "}
                  {EST_RECOVERIES} est. recoveries
                </motion.p>
              )}
            </div>

            {/* Rows */}
            <div className="relative min-h-0 flex-1 divide-y divide-primary/5 overflow-hidden">
              {visibleFlags === 0 && (
                <div className="absolute inset-0 flex items-center justify-center gap-1.5 text-[9px] text-primary/30">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Scanning for voidable transactions…
                </div>
              )}
              {FLAGS.map((flag, i) => {
                const isVisible = i < visibleFlags;
                const isTarget = flag.id === APPROVED_ID;
                const rowReviewing = isTarget && modalOpen;
                const rowAccepted = isTarget && accepted;
                return (
                  <motion.div
                    key={flag.id}
                    initial={false}
                    animate={
                      isVisible
                        ? { opacity: 1, height: "auto" }
                        : { opacity: 0, height: 0 }
                    }
                    transition={{ duration: 0.3 }}
                    className={cn(
                      "flex items-center gap-2 px-2.5 py-1 transition-colors",
                      isVisible ? "overflow-visible" : "overflow-hidden",
                      rowAccepted
                        ? "bg-emerald-50/70 dark:bg-emerald-500/10"
                        : rowReviewing
                          ? "bg-primary/[0.05]"
                          : "",
                    )}
                  >
                    {/* Checkbox */}
                    <span
                      className={cn(
                        "flex h-3 w-3 shrink-0 items-center justify-center rounded-[3px] border transition-colors",
                        rowAccepted
                          ? "border-emerald-500 bg-emerald-500"
                          : "border-primary/25 bg-white dark:bg-card",
                      )}
                    >
                      {rowAccepted && (
                        <Check className="h-2 w-2 text-white" strokeWidth={3} />
                      )}
                    </span>

                    {/* Counterparty + relationship */}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[10px] font-medium leading-tight text-foreground">
                        {flag.counterparty}
                      </p>
                      {flag.relationship && (
                        <p className="truncate text-[8.5px] leading-tight text-amber-700 dark:text-amber-400">
                          {flag.relationship}
                        </p>
                      )}
                    </div>

                    {/* Head badge */}
                    <span
                      className={cn(
                        "hidden shrink-0 rounded px-1 py-0.5 text-[8px] font-semibold sm:inline-block",
                        HEAD_STYLES[flag.head].chip,
                      )}
                    >
                      {flag.provision}
                    </span>

                    {/* Amount */}
                    <span className="shrink-0 font-mono text-[10px] font-semibold tabular-nums text-foreground/80">
                      {flag.amount}
                    </span>

                    {/* Decision badge */}
                    <span
                      className={cn(
                        "flex w-[58px] shrink-0 items-center justify-center gap-0.5 rounded px-1 py-0.5 text-[8px] font-semibold transition-colors",
                        rowAccepted
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                          : "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300",
                      )}
                    >
                      {rowAccepted && (
                        <Check className="h-2 w-2" strokeWidth={3} />
                      )}
                      {rowAccepted ? "Accepted" : "Pending"}
                    </span>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer ties the sample rows to the full identified count. */}
            {!analysing && (
              <div className="shrink-0 border-t border-primary/10 bg-primary/[0.02] px-2.5 py-1 text-[8.5px] font-medium text-primary/50">
                Showing {FLAGS.length} of {IDENTIFIED} potential voidable
                transactions
              </div>
            )}
          </div>

          {/* Review card — stylised TransactionReviewModal, centred over the app */}
          {modalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 p-3 backdrop-blur-[1px]"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="w-full max-w-[440px] overflow-hidden rounded-lg border border-primary/15 bg-white shadow-xl dark:bg-card"
              >
                {/* Header */}
                <div className="border-b border-primary/10 px-3 py-2">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-[11px] font-semibold text-foreground">
                          {REVIEW.name}
                        </p>
                        <span className="shrink-0 rounded bg-violet-100 px-1 py-0.5 text-[8px] font-semibold text-violet-700">
                          {REVIEW.provisions}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-[8.5px] text-amber-700 dark:text-amber-400">
                        {REVIEW.relationship}
                      </p>
                    </div>
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-primary/15 text-primary/40">
                      <X className="h-2.5 w-2.5" />
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1">
                    <span className="rounded bg-red-100 px-1 py-0.5 text-[8px] font-semibold text-red-700">
                      High Risk
                    </span>
                    <span className="rounded bg-gray-100 px-1 py-0.5 text-[8px] font-semibold text-gray-500 dark:bg-white/10 dark:text-gray-300">
                      Pending
                    </span>
                  </div>
                </div>

                {/* Amount + recoverable */}
                <div className="grid grid-cols-2 gap-2 px-3 py-2">
                  <div>
                    <p className="text-[7.5px] font-semibold uppercase tracking-wider text-primary/40">
                      Amount
                    </p>
                    <p className="font-mono text-[13px] font-bold tabular-nums text-foreground">
                      {REVIEW.amount}
                    </p>
                  </div>
                  <div>
                    <p className="text-[7.5px] font-semibold uppercase tracking-wider text-primary/40">
                      Recoverable estimate
                    </p>
                    <p className="font-mono text-[13px] font-bold tabular-nums text-foreground">
                      {REVIEW.recoverable}
                    </p>
                  </div>
                </div>

                {/* Agent assessment */}
                <div className="px-3 pb-2">
                  <p className="text-[7.5px] font-semibold uppercase tracking-wider text-primary/40">
                    Agent Assessment
                  </p>
                  <p className="mt-0.5 line-clamp-3 text-[9px] leading-relaxed text-foreground/70">
                    {REVIEW.assessment}
                  </p>
                  <div className="mt-1.5 flex items-center gap-1">
                    <span className="text-[7.5px] font-semibold uppercase tracking-wider text-primary/40">
                      Also captured by
                    </span>
                    {REVIEW.alsoCaptured.map((label) => (
                      <span
                        key={label}
                        className="rounded border border-primary/15 px-1 py-0.5 text-[8px] font-medium text-primary/60"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Footer — Accept / Reject / Ask Agent */}
                <div className="flex items-center gap-1.5 border-t border-primary/10 bg-primary/[0.02] px-3 py-2">
                  <motion.span
                    animate={
                      phase === "accepting"
                        ? { scale: [1, 0.9, 1] }
                        : { scale: 1 }
                    }
                    transition={{ duration: 0.3 }}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[9px] font-semibold text-white"
                  >
                    <Check className="h-2.5 w-2.5" /> Accept
                  </motion.span>
                  <span className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2 py-1 text-[9px] font-semibold text-red-600 dark:border-red-500/40 dark:text-red-400">
                    <X className="h-2.5 w-2.5" /> Reject
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-md border border-primary/15 px-2 py-1 text-[9px] font-medium text-primary/60">
                    <MessageSquare className="h-2.5 w-2.5" /> Ask Agent
                  </span>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Animated cursor for the review gesture */}
          {cursor.visible && (
            <motion.div
              className="pointer-events-none absolute z-40 drop-shadow"
              initial={false}
              animate={{ left: `${cursor.x}%`, top: `${cursor.y}px` }}
              transition={{ duration: 0.55, ease: "easeInOut" }}
            >
              <MousePointer2 className="h-4 w-4 fill-white text-foreground" />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
